'use client';

import React from 'react';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';
import AirbnbSplitCalculator from '../../components/blog/AirbnbSplitCalculator';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';
const OMBRE = 'linear-gradient(135deg, #6ee7b7, #06dffa, #dcccfe)';

const ombre: React.CSSProperties = {
  background: OMBRE,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
};

const link: React.CSSProperties = {
  ...ombre,
  textDecoration: 'underline',
  // The underline inherits `color`, which is transparent here, so it has to be
  // set explicitly or the link renders with no underline at all.
  textDecorationColor: '#06dffa',
  fontWeight: 600,
};

const h2: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: 1.35,
  color: 'white',
  margin: '36px 0 14px',
  // Same stack the help centre and the comparison page use for headings.
  fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
};

const p: React.CSSProperties = {
  fontSize: '16px',
  lineHeight: 1.8,
  color: 'rgba(255,255,255,0.8)',
  marginBottom: '18px',
};

const AirbnbSplitCalculatorContent = () => (
  <div
    style={{
      minHeight: '100vh',
      background:
        'linear-gradient(180deg, #0a0f32 0%, #0a1230 8%, #0c1a35 18%, #0e2240 30%, #102a48 45%, #0e2240 60%, #0c1a35 75%, #0a1230 88%, #0a0f32 100%)',
      color: 'white',
      fontFamily: FONT,
    }}
  >
    <PageHeader />
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '0 24px 48px' }}>
      {/* Same treatment as the blog's "Back to Tips" link. The arrow sits
          outside the clipped span: background-clip on a glyph at the edge of
          its box chops it mid-stroke. */}
      <a
        href="/calculators"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          textDecoration: 'none',
          fontSize: '13px',
          fontWeight: 600,
          margin: '24px 0',
        }}
      >
        <span style={{ WebkitTextFillColor: 'unset', color: 'white' }}>←</span>
        <span style={ombre}>Back to other calculators</span>
      </a>

      {/* Same treatment as the /calculators index and the help centre hero —
          the two pages in this section were introducing themselves in
          different voices. */}
      <h1
        style={{
          fontSize: '23px',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
          color: '#E8DEFF',
          marginTop: '24px',
          marginBottom: '20px',
        }}
      >
        Airbnb split calculator
      </h1>

      <p style={{ ...p, fontSize: '17px' }}>
        Sometimes an even split doesn't cut it. Enter what your Airbnb costs and each open spot
        on the trip's accommodations: private bathroom? Bed or couch? Sharing or private room to
        themselves?
      </p>
      {/* Split from the sentence above: the first half is what you put in, the
          second is what you get back, and running them together buried the
          payoff mid-paragraph. */}
      <p style={{ ...p, fontSize: '17px' }}>
        This calculator works out what each spot in the Airbnb should cost, weighted by the three
        things that matter most. And it gives you a message you can paste straight into the group
        chat so you can decide who goes where before booking.
      </p>
      {/* Deliberately no privacy claim here. Session replay runs with
          maskAllInputs:false (components/common/PostHogProvider.tsx), so on a
          sampled session rrweb records these fields verbatim — "nothing you
          type leaves your browser" was false. Add .ph-mask to the inputs
          before claiming otherwise. */}
      <p style={p}>It's free, and there's no signup required.</p>

      <AirbnbSplitCalculator />

      <h2 style={h2}>How the split works</h2>
      <p style={p}>
        The total is divided into two pots. The first is shared space: the kitchen, the living
        area, the bathrooms everyone uses. That is split evenly by headcount, because everyone
        uses it the same amount. You choose which of those the place actually has, and together
        they account for at most 20% of the cost.
      </p>
      <p style={p}>
        The rest is sleeping arrangements, and that's where the spots differ. Each one is
        weighted (1 vs 2 weight) on whether the room has its own bathroom and whether it's a
        real bed or a couch. If the spot is a shared area so whoever takes it does not get a
        private room, the summed weights are divided by 2. A spot with a private space, private
        bathroom and its own bed carries the most; a couch in a common room carries the least.
        Cleaning and service fees, if you enter them, are split evenly on top, because everyone
        triggers those equally.
      </p>

      <h2 style={h2}>When you don't need this</h2>
      <p style={p}>
        Most groups should just split evenly, and most of them do. If the bedrooms are broadly
        comparable and you're there two or three nights, the gap between an even split and a
        perfectly fair one is usually twenty or thirty dollars a head, less than the
        conversation costs. It's worth doing the math when one room is a genuinely different
        experience, when it's a long stay, or when someone on the trip is watching their budget.
      </p>
      <p style={p}>
        The longer version of that argument, along with what to do about late arrivals, kids and
        the person who fronted the booking, is in{' '}
        <a style={link} href="/tips/how-to-split-an-airbnb-with-unequal-rooms">
          how to split an Airbnb when the rooms aren't equal
        </a>
        .
      </p>

      <h2 style={h2}>Then there's the rest of the trip</h2>
      <p style={p}>
        The house is one number, agreed once. The bulk of the splitting is everything after it: groceries, dinners, the boat rental, the person who covered gas twice. Tabster keeps a
        running tab
        for the whole trip: everyone adds receipts as they go, claims what they actually had,
        and it settles at the end without a spreadsheet.
      </p>
      <p style={p}>
        See{' '}
        <a style={link} href="/trips">
          how trip mode works
        </a>
        , or{' '}
        <a style={link} href="/support/getting-started/splitting-your-first-group-trip">
          how to split expenses on a group trip
        </a>
        .
      </p>
    </div>
    <PageFooter subtitle="Free download. No manual inputs. Just fun 🤠" />
  </div>
);

export default AirbnbSplitCalculatorContent;
